import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Upload, CircleX } from "lucide-react";
import { getDocType, getDocSubType } from "../../api/DocumentAPI";

const UploadDocumentModal = ({
  open,
  onClose,
  selectedDocStage,
  onUpload,
  replaceMode = false,
  replaceDocData = null,
}) => {
  const [docTypes, setDocTypes] = useState([]);
  const [docSubTypes, setDocSubTypes] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [selectedDocSubType, setSelectedDocSubType] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const user_id = localStorage.getItem("userId")
  // Fetch document types
  const fetchDocTypes = async () => {
    try {
      const types = await getDocType();
      const data = types?.data || [];
      setDocTypes(data);

      if (selectedDocStage && !replaceMode) {
        const matchedType = data.find(
          (t) => t.document_stage === selectedDocStage
        );
        if (matchedType) setSelectedDocType(matchedType.id);
      }
    } catch (err) {
      console.error("Failed to fetch document types:", err);
    }
  };

  // Fetch subtypes
  const fetchSubTypes = async (typeId) => {
    if (!typeId) {
      setDocSubTypes([]);
      return;
    }
    try {
      const payload = { doc_type: Number(typeId) };
      const res = await getDocSubType(payload);
      setDocSubTypes(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch document subtypes:", err);
      setDocSubTypes([]);
    }
  };

  // ✅ Pre-fill data when replacing a document
  useEffect(() => {
    if (replaceMode && replaceDocData) {
      setSelectedDocType(replaceDocData.typeId || "");
      fetchSubTypes(replaceDocData.typeId); // load subtype list first
      setSelectedDocSubType(replaceDocData.subtypeId || "");
    } else {
      setSelectedDocType(selectedDocStage || "");
      setSelectedDocSubType("");
    }
  }, [replaceMode, replaceDocData, selectedDocStage]);

  // Fetch doc types when popup opens
  useEffect(() => {
    if (open) fetchDocTypes();
  }, [open]);

  // Fetch subtypes when doc type changes
  useEffect(() => {
    if (selectedDocType) fetchSubTypes(selectedDocType);
    else setDocSubTypes([]);
  }, [selectedDocType]);

  // File selection handler
  const handleFileChange = (e) => {
    const input = e.target;
    const selectedFiles = Array.from(input.files || []);

    if (!selectedDocType || !selectedDocSubType) {
      setError("Please select document type and subtype first.");
      input.value = "";
      return;
    }

    const file = selectedFiles[0];
    if (!file) return;

    const isPDF =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    const isValidSize = file.size <= 5 * 1024 * 1024;

    if (!isPDF) {
      setError("Only PDF files are allowed.");
      input.value = "";
      return;
    }

    if (!isValidSize) {
      setError(`"${file.name}" exceeds 5 MB size limit.`);
      input.value = "";
      return;
    }

    setFile(file);
    input.value = "";
  };

  const handleRemoveFile = () => setFile(null);

  const handleUpload = () => {
    if (!file || !selectedDocSubType) return;


    const formData = new FormData();
    formData.append("user_id", user_id);
    formData.append("document_type", selectedDocSubType);
    formData.append("file", file); // ✅ File object directly
    formData.append("document_stage", selectedDocType);
    onUpload(formData);
    onClose();
  };

  const handleCloseSnackbar = () => setError("");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className="bg-[#572575] font-siromi text-xl font-medium text-white flex justify-between items-center">
        <span className="flex gap-2">
          <Upload className="bg-[#FFFFFF26] p-1 rounded-md" />{" "}
          {replaceMode ? "Replace Document" : "Upload Documents"}
        </span>
        <IconButton onClick={onClose} className="text-white">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} className="mt-2">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="dense" disabled={replaceMode}>
              <InputLabel id="doc-type-label">Document Type</InputLabel>
              <Select
                labelId="doc-type-label"
                value={selectedDocType}
                onChange={(e) => setSelectedDocType(e.target.value)}
                label="Document Type"
              >
                {docTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.document_stage}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              margin="dense"
              disabled={!selectedDocType || replaceMode}
            >
              <InputLabel id="doc-subtype-label">Document Subtype</InputLabel>
              <Select
                labelId="doc-subtype-label"
                value={selectedDocSubType}
                onChange={(e) => setSelectedDocSubType(e.target.value)}
                label="Document Subtype"
              >
                {docSubTypes.map((subType) => (
                  <MenuItem key={subType.id} value={subType.id}>
                    {subType.document_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <div className="border-dashed border-2 border-gray-300 rounded-lg p-6 text-center mt-4">
          <input
            type="file"
            id="fileUpload"
            className="hidden"
            onChange={handleFileChange}
            accept=".pdf"
            disabled={!selectedDocType || !selectedDocSubType}
          />
          <label
            htmlFor="fileUpload"
            className={`cursor-pointer text-sm ${!selectedDocType || !selectedDocSubType
                ? "text-gray-400 cursor-not-allowed pointer-events-none"
                : "text-purple-700"
              }`}
          >
            Drag & Drop or Click to Upload
          </label>
          <p className="text-xs text-gray-500">
            Supported format: PDF only (Max: 5MB)
          </p>
        </div>

        {file && (
          <div className="mt-4">
            <span className="font-altisunisa text-[#572575] font-medium">
              Selected File:
            </span>
            <div className="flex justify-between items-center bg-gray-100 p-2 rounded-md mt-2">
              <span className="text-sm">{file.name}</span>
              <CircleX
                className="text-red-500 cursor-pointer"
                onClick={handleRemoveFile}
              />
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions className="flex justify-center gap-4 mb-4">
        <Button
          onClick={onClose}
          className="bg-[#572575] hover:bg-[#572575] text-white font-altisunisa text-base px-4 py-2 rounded-lg"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedDocType || !selectedDocSubType || !file}
          className="bg-[#572575] hover:bg-[#572575] text-white font-altisunisa text-base px-4 py-2 rounded-lg"
        >
          {replaceMode ? "Replace File" : "Upload"}
        </Button>
      </DialogActions>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default UploadDocumentModal;
